// src/axiosInstance.js
import axios from 'axios';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  timeout: 10000,
  headers: {
    'X-Client-Version': process.env.REACT_APP_VERSION
  }
});

// Simplified token getter
const getToken = () => {
  try {
    const tokenSources = [
      () => JSON.parse(localStorage.getItem('user'))?.token,
      () => sessionStorage.getItem('token'),
      () => localStorage.getItem('token')
    ];

    for (const source of tokenSources) {
      const token = source();
      if (token) return token;
    }
    return null;
  } catch (e) {
    alert('⚠️ Your session has expired. Please log in again.');
    return null;
  }
};

// Clean request interceptor
instance.interceptors.request.use(config => {
  if (!config.url.includes('/auth/') && !config.url.includes('/enter')) {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Essential response interceptor
instance.interceptors.response.use(
  response => response,
  error => {
    // Handle token expiration
    if (error.response?.data?.code === "TOKEN_EXPIRED") {
      localStorage.removeItem('token');
      alert('🔒 Your session has expired. Please log in again.');
      window.location.href = '/';
      return Promise.reject(error);
    }

    // Handle network errors
    if (error.code === 'ECONNABORTED' || !error.response) {
      alert('🌐 Your device is unable to connect to our network! Please check your connection and try again.');
      return Promise.reject(error);
    }

    // Handle 401 errors
    if (error.response.status === 401) {
      localStorage.removeItem('token');
      alert('🔐 Session Authentication Failed. Please log in again.');
      window.location.href = '/';
      return Promise.reject(error);
    }

    // Handle server errors
    if (error.response.status >= 500) {
      alert('🚨 Our servers are undergoing temporary maintenance and updates! Please try again later.');
    }

    return Promise.reject(error);
  }
);

// Version check interceptor
instance.interceptors.request.use(async (config) => {
  if (!config.url.includes('version.json')) {
    try {
      const versionResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/data/version.json`);
      if (versionResponse.data.version !== process.env.REACT_APP_VERSION) {
        alert('🔄 System update required.');
        window.location.reload(true);
      }
    } catch (e) {
      alert('⚠️ Failed to load system-wide updates. Please clear your browser history and refresh the page.');
    }
  }
  return config;
});

export default instance;